// v192 — Per-project Auto-Quarantine settings page (item 16 of
// docs/PRODUCT-ROADMAP.md, "Auto-Quarantine bei neuer CVE").
//
// Renders a small settings card with four knobs:
//
//   1. Enabled toggle
//   2. Severity-threshold picker (LOW / MEDIUM / HIGH / CRITICAL)
//   3. Auto-block duration in days
//   4. Notify-emails list (comma-separated input → chips)
//
// Saving PUTs the project's row in project_quarantine_settings; the
// background worker (api/internal/security/auto_quarantine.go) picks
// up the change on its next tick (5 min).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Save, AlertTriangle, X } from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useProjects } from '../../api/client';
import {
  type QuarantineSeverity,
  type QuarantineSettings,
  putQuarantineSettings,
  useQuarantineSettings,
} from '../../api/quarantine';

const SEVERITIES: QuarantineSeverity[] = ['low', 'medium', 'high', 'critical'];

const defaults: QuarantineSettings = {
  enabled: false,
  severity_threshold: 'high',
  auto_block_until_days: 30,
  notify_emails: [],
};

const selectClass =
  'h-9 px-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

export function Quarantine() {
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  const { data, error, mutate, isLoading } = useQuarantineSettings(id);
  const baseline: QuarantineSettings = useMemo(() => data ?? defaults, [data]);

  // Local draft state — copy server state on first load, then keep
  // the user's edits until save or cancel.
  const [enabled, setEnabled] = useState<boolean>(baseline.enabled);
  const [threshold, setThreshold] = useState<QuarantineSeverity>(
    baseline.severity_threshold,
  );
  const [days, setDays] = useState<number>(baseline.auto_block_until_days);
  const [emails, setEmails] = useState<string[]>(baseline.notify_emails);
  const [emailInput, setEmailInput] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Sync server → draft on first load (and after a successful save).
  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setThreshold(data.severity_threshold);
      setDays(data.auto_block_until_days);
      setEmails(data.notify_emails);
    }
  }, [data]);

  const dirty =
    enabled !== baseline.enabled ||
    threshold !== baseline.severity_threshold ||
    days !== baseline.auto_block_until_days ||
    JSON.stringify(emails) !== JSON.stringify(baseline.notify_emails);

  const isForbidden = error && String(error).includes('403');

  const addEmails = (raw: string) => {
    const parts = raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    const lower = new Set(emails.map((e) => e.toLowerCase()));
    const next = [...emails];
    for (const p of parts) {
      if (!lower.has(p.toLowerCase())) {
        next.push(p);
        lower.add(p.toLowerCase());
      }
    }
    setEmails(next);
    setEmailInput('');
  };

  const removeEmail = (idx: number) => {
    setEmails(emails.filter((_, i) => i !== idx));
  };

  const onSave = async () => {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      await putQuarantineSettings(id, {
        enabled,
        severity_threshold: threshold,
        auto_block_until_days: days,
        notify_emails: emails,
      });
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCancel = () => {
    setEnabled(baseline.enabled);
    setThreshold(baseline.severity_threshold);
    setDays(baseline.auto_block_until_days);
    setEmails(baseline.notify_emails);
    setEmailInput('');
    setErr(null);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to projects
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">
          {project?.slug ?? id} · Security
        </p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          Auto-quarantine
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          Automatically apply a security block when a new scan finding at or
          above the configured severity lands on an artifact in this project.
          Blocks expire after the configured window unless an admin extends
          them. Pulls already in flight are not interrupted.
        </p>
      </div>

      {isForbidden && (
        <Card className="border-warning/30 bg-warning/5">
          <CardBody className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
            <div className="text-sm">
              You don't have permission to view this project's auto-quarantine
              settings. Ask a project maintainer to make changes.
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-accent" />
              Settings
            </span>
          }
          description="Bridge open scan_findings into security_blocks so the gate fires automatically."
        />
        <CardBody className="space-y-5">
          <div className="flex items-start gap-3">
            <input
              id="enabled"
              type="checkbox"
              className="h-4 w-4 mt-1"
              disabled={!!isForbidden || isLoading}
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <label htmlFor="enabled" className="text-sm">
              <span className="font-medium text-fg">Enable auto-quarantine</span>
              <span className="block text-fg-muted">
                When off, the worker skips this project entirely — no blocks
                are created regardless of finding severity.
              </span>
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="threshold"
                className="block text-xs font-medium text-fg-muted mb-1"
              >
                Severity threshold
              </label>
              <select
                id="threshold"
                className={selectClass}
                disabled={!!isForbidden || isLoading}
                value={threshold}
                onChange={(e) =>
                  setThreshold(e.target.value as QuarantineSeverity)
                }
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s.toUpperCase()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-fg-muted mt-1">
                Findings at or above this severity trigger a block.
              </p>
            </div>
            <div>
              <label
                htmlFor="days"
                className="block text-xs font-medium text-fg-muted mb-1"
              >
                Block duration (days)
              </label>
              <Input
                id="days"
                type="number"
                min={1}
                max={365}
                disabled={!!isForbidden || isLoading}
                value={days}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value || '0', 10);
                  setDays(Number.isFinite(n) ? n : 0);
                }}
              />
              <p className="text-xs text-fg-muted mt-1">
                Auto-blocks expire after this window. Max 365.
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="emails"
              className="block text-xs font-medium text-fg-muted mb-1"
            >
              Notify emails
            </label>
            <div className="flex gap-2">
              <Input
                id="emails"
                placeholder="team@example.com, sec@example.com"
                disabled={!!isForbidden || isLoading}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addEmails(emailInput);
                  }
                }}
                onBlur={() => emailInput && addEmails(emailInput)}
              />
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {emails.map((e, idx) => (
                  <span
                    key={`${e}-${idx}`}
                    className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-bg-inset text-xs text-fg-muted border border-border"
                  >
                    {e}
                    <button
                      type="button"
                      className="hover:text-fg"
                      onClick={() => removeEmail(idx)}
                      aria-label={`Remove ${e}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-fg-muted mt-1">
              Recipients also receive an email when a block is auto-created.
              Webhook subscribers see a <code>block.auto_quarantine.created</code>{' '}
              event regardless of this list.
            </p>
          </div>

          {data?.updated_at && (
            <p className="text-xs text-fg-muted">
              Last updated {new Date(data.updated_at).toLocaleString()}
              {data.updated_by_email ? ` by ${data.updated_by_email}` : ''}.
            </p>
          )}

          {err && (
            <div className="text-sm text-danger">{err}</div>
          )}
        </CardBody>
        <CardFooter className="flex justify-end gap-2">
          <Badge tone={enabled ? 'success' : 'neutral'}>
            {enabled ? 'Active' : 'Disabled'}
          </Badge>
          <div className="flex-1" />
          <Button
            variant="ghost"
            disabled={!dirty || busy}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={!dirty || busy || !!isForbidden}
            onClick={onSave}
          >
            <Save className="h-4 w-4 mr-1.5" />
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
